'use client';

import { Package } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/navigation';
import { useRepScoreStore } from '@/presentation/hooks/useStores';
import {
  ScoreCard,
  StatusTile,
  DrawerWhy,
  DrawerCTA,
  DEFAULT_DRAWER_WHY_COPY,
} from '../shared';

interface HighTrustStateProps {
  onStartPlan?: () => void;
}

/**
 * STATE 3: Benefit available (eligible for future plans or already active)
 *
 * Uses the backend-derived Early Pickup account status instead of score-only
 * buckets, so the copy stays truthful when a user already has an active
 * Early Pickup plan but is not yet re-qualified for future plans.
 */
export const HighTrustState = observer(function HighTrustState({
  onStartPlan,
}: HighTrustStateProps) {
  const router = useRouter();
  const repScoreStore = useRepScoreStore();
  const score = repScoreStore.currentScore;
  const tier = repScoreStore.trustTier;
  const tierConfig = repScoreStore.tierConfig;
  const earlyPickupStatus = repScoreStore.earlyPickupStatus;
  const hasActiveEarlyPickupPlan =
    earlyPickupStatus?.hasActiveEarlyPickupPlan === true;
  const eligibleForFuturePlans =
    earlyPickupStatus?.eligibleForFuturePlans === true;
  const onTime = repScoreStore.scoreBreakdown?.paymentHistory?.onTimePayments ?? 0;
  const helper = `${onTime} on-time ${onTime === 1 ? 'payment' : 'payments'} on your record. Thanks for paying on time.`;
  const statusTitle = eligibleForFuturePlans
    ? 'Early Pickup is available for you'
    : hasActiveEarlyPickupPlan
      ? 'Early Pickup is active on one of your plans'
      : 'Early Pickup not eligible yet';
  const statusBody = eligibleForFuturePlans
    ? hasActiveEarlyPickupPlan
      ? `Early Pickup is active on a current plan, and you can collect at ${tierConfig.pickupThreshold}% paid on your next one too.`
      : `Collect at ${tierConfig.pickupThreshold}% paid on your next plan.`
    : hasActiveEarlyPickupPlan
      ? 'You already have Early Pickup on an active plan. Keep paying on time to keep building toward future plans.'
      : "Keep paying on time. We'll let you know when you qualify.";

  const handleShop = () => {
    onStartPlan?.();
    router.push('/');
  };

  return (
    <div className="flex flex-col gap-[18px]">
      <ScoreCard score={score} tier={tier} helper={helper} />

      <StatusTile
        variant="success"
        icon={<Package className="size-4 text-white" />}
        title={statusTitle}
        body={statusBody}
      />

      <DrawerWhy body={DEFAULT_DRAWER_WHY_COPY} />

      <DrawerCTA
        label={eligibleForFuturePlans ? 'Shop items' : 'Got it'}
        onClick={eligibleForFuturePlans ? handleShop : onStartPlan}
        variant={eligibleForFuturePlans ? 'blue' : 'black'}
      />
    </div>
  );
});

export default HighTrustState;
